<template>
	<div class="date-range-selector">
		<!-- Date Range Button -->
		<v-btn
			outlined
			class="date-range-btn text-none"
			:class="buttonClass"
			@click="showModal = true"
		>
			<v-icon left>
				mdi-calendar-range
			</v-icon>
			<span class="ml-2 text-caption">
				{{ displayText }}
			</span>
		</v-btn>

		<!-- Date Range Modal -->
		<v-dialog
			v-model="showModal"
			max-width="650px"
			persistent
		>
			<v-card>
				<v-card-title class="pb-2">
					<span class="text-h6">
						Selected Date Range: {{ formatDateRange() }}
					</span>
					<v-spacer />
					<v-btn
						icon
						@click="closeModal"
					>
						<v-icon small>
							mdi-close
						</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider class="mb-2" />

				<v-card-text>
					<v-container>
						<v-row>
							<!-- Custom Date Range - Left Column -->
							<v-col cols="6">
								<div class="text-subtitle-1 mb-3">
									Custom Date Range
								</div>
								<div class="d-flex justify-center">
									<v-date-picker
										v-model="dateRange"
										range
										:max="todayDate"
										color="primary"
										class="custom-date-picker"
										@input="onDatePickerChange"
									/>
								</div>
							</v-col>

							<!-- Quick Period Selection - Right Column -->
							<v-col cols="6">
								<div class="text-subtitle-1 mb-3">
									Quick Select
								</div>
								<v-row dense>
									<v-col
										v-for="periodOption in periodOptions"
										:key="periodOption.value"
										cols="12"
										class="mb-2"
									>
										<v-card
											outlined
											hover
											class="period-card"
											:class="{ 'period-card--selected': selectedPeriod === periodOption.value }"
											@click="selectQuickPeriod(periodOption.value)"
										>
											<v-card-text class="d-flex align-center py-2">
												<v-icon
													left
													class="period-card__icon"
												>
													{{ periodOption.icon }}
												</v-icon>
												<span class="text-body-2 period-card__text">
													{{ periodOption.text }}
												</span>
											</v-card-text>
										</v-card>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</v-container>
				</v-card-text>

				<v-divider class="mb-2" />
				<v-card-actions>
					<v-spacer />
					<SecondaryActionButton @click="closeModal">
						Cancel
					</SecondaryActionButton>
					<PrimaryActionButton
						class="ml-2"
						@click="applyDateRange"
					>
						Apply
					</PrimaryActionButton>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import PrimaryActionButton from './primary-action-button.vue'
import SecondaryActionButton from './secondary-action-button.vue'
import { Periods, PeriodOptions, calculateDatesForPeriod } from '../constants/periods.js'

export default {
	name: 'date-range-selector',
	components: {
		PrimaryActionButton,
		SecondaryActionButton
	},
	props: {
		/**
		 * Start date in YYYY-MM-DD format
		 */
		startDate: {
			type: String,
			default: ''
		},
		/**
		 * End date in YYYY-MM-DD format
		 */
		endDate: {
			type: String,
			default: ''
		},
		/**
		 * Current period selection (from Periods enum)
		 */
		period: {
			type: Number,
			default: null
		},
		/**
		 * Optional CSS class for the button
		 */
		buttonClass: {
			type: String,
			default: ''
		},
		/**
		 * Label to show when no dates selected
		 */
		placeholder: {
			type: String,
			default: 'Select dates'
		}
	},
	data () {
		return {
			showModal: false,
			selectedPeriod: null,
			dateRange: [],
			isInitialising: false,
			isUpdatingFromPeriod: false,
			userHasInteractedWithCalendar: false,
			periodOptions: PeriodOptions,
			Periods
		}
	},
	computed: {
		todayDate () {
			return new Date().toISOString().split('T')[0]
		},
		displayText () {
			if (this.startDate && this.endDate) {
				return this.formatDisplayDateRange(this.startDate, this.endDate)
			}
			return this.placeholder
		}
	},
	watch: {
		showModal (newValue) {
			if (newValue) {
				this.initialiseValues()
			}
		}
	},
	methods: {
		initialiseValues () {
			this.isInitialising = true
			this.isUpdatingFromPeriod = false

			if (!this.userHasInteractedWithCalendar) {
				if (this.period !== null && this.period !== undefined && this.period !== Periods.Custom) {
					this.selectedPeriod = this.period
				} else if (this.period === Periods.Custom) {
					this.selectedPeriod = Periods.Custom
				} else {
					this.selectedPeriod = null
				}
			}

			if (this.startDate && this.endDate) {
				this.dateRange = [this.startDate, this.endDate]
			} else {
				this.dateRange = []
			}

			this.$nextTick(() => {
				this.isInitialising = false
			})
		},

		selectQuickPeriod (period) {
			this.userHasInteractedWithCalendar = false
			this.selectedPeriod = period
			this.isUpdatingFromPeriod = true

			const dates = calculateDatesForPeriod(period)
			if (dates) {
				this.dateRange = [dates.startDate, dates.endDate]
			}

			this.$nextTick(() => {
				this.isUpdatingFromPeriod = false
			})
		},

		onDatePickerChange (newRange) {
			if (Array.isArray(newRange) && newRange.length === 2) {
				const [d1, d2] = newRange
				if (d1 && d2 && new Date(d2) < new Date(d1)) {
					this.dateRange = [d2, d1]
				}
			}

			if (!this.isInitialising && !this.isUpdatingFromPeriod) {
				if (newRange && newRange.length >= 1) {
					this.userHasInteractedWithCalendar = true
					this.selectedPeriod = Periods.Custom
				}
			}
		},

		formatDateRange () {
			if (!Array.isArray(this.dateRange) || this.dateRange.length === 0) {
				return 'No date range selected'
			}

			if (this.dateRange.length === 1 || !this.dateRange[1]) {
				const onlyDate = this.dateRange[0]
				return onlyDate ? this.formatDateToYMD(onlyDate) : 'No date range selected'
			}

			let start = this.dateRange[0]
			let end = this.dateRange[1]
			if (start && end && new Date(end) < new Date(start)) {
				[start, end] = [end, start]
			}
			return `${this.formatDateToYMD(start)} - ${this.formatDateToYMD(end)}`
		},

		formatDateToYMD (dateString) {
			const date = new Date(dateString)
			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			return `${year}/${month}/${day}`
		},

		formatDisplayDateRange (startDate, endDate) {
			const start = new Date(startDate)
			const end = new Date(endDate)

			const startFormatted = start.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: 'numeric'
			})
			const endFormatted = end.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: 'numeric'
			})

			return `${startFormatted} - ${endFormatted}`
		},

		applyDateRange () {
			let startDate = this.dateRange && this.dateRange[0] ? this.dateRange[0] : ''
			let endDate = this.dateRange && this.dateRange[1] ? this.dateRange[1] : this.dateRange[0]

			if (startDate && endDate) {
				if (new Date(endDate) < new Date(startDate)) {
					const tmp = startDate
					startDate = endDate
					endDate = tmp
					this.dateRange = [startDate, endDate]
				}
			}

			let periodToEmit = this.selectedPeriod
			if (this.userHasInteractedWithCalendar && this.selectedPeriod === Periods.Custom) {
				periodToEmit = Periods.Custom
			}

			this.$emit('apply', {
				period: periodToEmit,
				startDate,
				endDate
			})

			// Also emit individual updates for v-model style binding
			this.$emit('update:startDate', startDate)
			this.$emit('update:endDate', endDate)
			this.$emit('update:period', periodToEmit)

			this.closeModal()
		},

		closeModal () {
			this.showModal = false
			// Reset interaction flag so next open restores state from props
			this.userHasInteractedWithCalendar = false
		}
	}
}
</script>

<style scoped>
.date-range-selector {
	display: inline-block;
}

.date-range-btn {
	height: 40px;
	min-width: 200px;
}

/* Period card - default (unselected) state */
.period-card {
	cursor: pointer;
	transition: all 0.2s ease;
	border-radius: var(--radius, 8px) !important;
	background-color: var(--global-card-background, #ffffff) !important;
}

.period-card:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12) !important;
}

.period-card .v-card__text {
	padding: 8px 12px !important;
}

.period-card__icon {
	margin-right: 8px;
	color: var(--global-primary, #406A94) !important;
}

.period-card__text {
	color: var(--global-foreground, #4a5568);
}

/* Period card - selected state */
.period-card--selected {
	background-color: var(--global-primary, #406A94) !important;
	border-color: var(--global-primary, #406A94) !important;
}

.period-card--selected .period-card__icon {
	color: #ffffff !important;
}

.period-card--selected .period-card__text {
	color: #ffffff;
}

/* Custom styling for v-date-picker header */
.custom-date-picker ::v-deep .v-date-picker-header {
	background-color: var(--global-syrenis-grey) !important;
	color: var(--global-syrenis-black) !important;
}

.custom-date-picker ::v-deep .v-date-picker-header__value {
	background-color: var(--global-syrenis-grey) !important;
	color: var(--global-syrenis-black) !important;
}

.custom-date-picker ::v-deep .v-date-picker-title,
.custom-date-picker ::v-deep .v-picker__title {
	background-color: var(--global-syrenis-grey) !important;
	color: var(--global-syrenis-black) !important;
}

.custom-date-picker ::v-deep .v-picker__title {
	display: none;
}

.custom-date-picker ::v-deep .v-date-picker-title__btn {
	color: var(--global-syrenis-black) !important;
}

.custom-date-picker ::v-deep .v-date-picker-header .v-btn {
	color: var(--global-syrenis-black) !important;
}

.custom-date-picker ::v-deep .v-date-picker-header .v-btn .v-icon {
	color: var(--global-syrenis-black) !important;
}

/* Custom styling for selected dates in v-date-picker */
.custom-date-picker ::v-deep .v-date-picker-table .v-btn.v-btn--active .v-btn__content {
	color: #fff !important;
}

.custom-date-picker ::v-deep .v-date-picker-table .v-btn--active .v-btn__content {
	color: #fff !important;
}

.custom-date-picker ::v-deep .v-date-picker-table__current .v-btn__content {
	color: var(--global-syrenis-black) !important;
}

/* Date picker body background for dark mode */
.custom-date-picker ::v-deep .v-picker__body {
	background-color: var(--global-card-background, #ffffff) !important;
}

.custom-date-picker ::v-deep .v-date-picker-table {
	background-color: var(--global-card-background, #ffffff) !important;
}

/* Day buttons text colour */
.custom-date-picker ::v-deep .v-date-picker-table .v-btn:not(.v-btn--active) .v-btn__content {
	color: var(--global-foreground, #4a5568) !important;
}

/* Weekday headers */
.custom-date-picker ::v-deep .v-date-picker-table th {
	color: var(--global-muted-foreground, #64748b) !important;
}
</style>
